import React, { useEffect, useId, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, Input, Label, Row } from "reactstrap";
import { Catalogue, CatalogueBrandMode, CatalogueSharingMode, CatalogueViewerGate } from "../../types/catalogue.types";
import { isCatalogueResellerSharingEnabled } from "../../helpers/catalogueResellerSharingEnabled";
import { isCatalogueViewerGateEnabled } from "../../helpers/catalogueViewerGateEnabled";
import { VIEWER_GATE_OPTIONS, VIEWER_GATE_SHORT } from "./catalogueViewerGate";
import notifications from "../../common/Components/Notifications/Notification";
import ShareLinkExpiryFields from "./ShareLinkExpiryFields";
import ShareCountryPhoneFields from "./ShareCountryPhoneFields";
import SharePartyMatch from "./SharePartyMatch";
import ShareAccessPasswordOnce from "./ShareAccessPasswordOnce";
import ShareLinkReady from "./ShareLinkReady";
import { searchParties, type PartyHit } from "./ShareRecipientNameField";
import {
  createCatalogueShareLink,
  fetchCatalogueShareLinkDefaults,
  type CatalogueShareLinkDefaults,
} from "./catalogueSecurityApi";
import { CATALOGUE_PRICE_PRESETS } from "./cataloguePricing";
import {
  DEFAULT_SHARE_COUNTRY_ISO,
  formatSharePhoneDisplay,
  isIndiaShareCountry,
  isPlausibleSharePhone,
  isValidShareEmail,
  partyPhoneToShareFields,
} from "./catalogueShareCountries";
import { shareLinkPublicUrl } from "./shareLinkPublicUrl";
import {
  DEFAULT_SHARE_EXPIRY_AMOUNT,
  DEFAULT_SHARE_EXPIRY_UNIT,
  shareLinkExpiryLabel,
  shareLinkExpiryPayload,
  type ShareExpiryUnit,
} from "./shareLinkExpiry";
import "./resellerStudio.css";
import "./shareFlow.css";

interface ShareInviteModalProps {
  catalogue: Catalogue;
  isOpen: boolean;
  onClose: () => void;
}

/** Who the link is for. `partyId` is set when they are already in Parties. */
type Recipient = {
  partyId?: string;
  name: string;
  phone: string;
  countryIso: string;
  email: string;
  isReseller: boolean;
  forwardLimit: number;
};

type Created = {
  url: string;
  password: string;
  emailed: boolean;
  whatsApped: boolean;
  open: boolean;
  /** What the open link asks each viewer first. */
  gate?: CatalogueViewerGate;
  recipient: Recipient;
};

/** Automatic WhatsApp invite (backend hkp.whatsapp). Shown only where it's switched on. */
const whatsAppInvitesOn = () => process.env.REACT_APP_WHATSAPP_ENABLED === "true";

const PRICE_TIER_OPTIONS = ["", ...CATALOGUE_PRICE_PRESETS];
const DEFAULT_FORWARD_LIMIT = 3;

const EMPTY: Recipient = {
  name: "",
  phone: "",
  countryIso: DEFAULT_SHARE_COUNTRY_ISO,
  email: "",
  isReseller: false,
  forwardLimit: DEFAULT_FORWARD_LIMIT,
};

/** Where the starting settings came from, said under "Link settings". */
type DefaultsState = { status: "loading" } | { status: "last"; at: string } | { status: "first" } | { status: "failed" };

const shortDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const firstName = (name: string) => name.trim().split(/\s+/)[0] || "them";
const looksLikePhone = (q: string) => /^[+\d][\d\s-]{3,}$/.test(q.trim());

export function recipientFromParty(p: PartyHit): Recipient {
  const fields = partyPhoneToShareFields(p.phone);
  return {
    partyId: p.id,
    name: p.name,
    phone: fields.phone,
    countryIso: p.phone ? fields.countryIso : DEFAULT_SHARE_COUNTRY_ISO,
    email: p.email || "",
    isReseller: Boolean(p.isReseller),
    forwardLimit: p.forwardLimit || DEFAULT_FORWARD_LIMIT,
  };
}

/**
 * Share a catalogue in one pass: find the person (a saved party, or a new one by name or mobile),
 * choose what they may do with it, then create the link and send it.
 */
const ShareInviteModal: React.FC<ShareInviteModalProps> = ({ catalogue, isOpen, onClose }) => {
  const resellerSharingOn = isCatalogueResellerSharingEnabled();
  // Open links can ask each viewer who they are (name, mobile, OTP) before opening.
  const viewerGateOn = resellerSharingOn && isCatalogueViewerGateEnabled();
  // The catalogue's mode only picks the starting choice. Each link keeps what it was sent with.
  const defaultAccess: CatalogueSharingMode = catalogue.sharingMode || "EXCLUSIVE";
  const [access, setAccess] = useState<CatalogueSharingMode>(defaultAccess);
  // First open link asks name and mobile (same as the backend's first-link default).
  const firstGate: CatalogueViewerGate = viewerGateOn ? "PHONE" : "NONE";
  const [viewerGate, setViewerGate] = useState<CatalogueViewerGate>(firstGate);
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [brandMode, setBrandMode] = useState<CatalogueBrandMode>("RESELLER");
  const [typedPassword, setTypedPassword] = useState("");
  const [generatePassword, setGeneratePassword] = useState(false);
  const [sendInviteEmail, setSendInviteEmail] = useState(false);
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  const [priceTier, setPriceTier] = useState("");
  const [expiryAmount, setExpiryAmount] = useState<number>(DEFAULT_SHARE_EXPIRY_AMOUNT);
  const [expiryUnit, setExpiryUnit] = useState<ShareExpiryUnit>(DEFAULT_SHARE_EXPIRY_UNIT);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);
  const [defaults, setDefaults] = useState<DefaultsState>({ status: "loading" });
  // Settings the studio changed before the last-link values arrived stay as they set them.
  const touched = useRef<Set<string>>(new Set());
  const touch = (key: string) => touched.current.add(key);

  useEffect(() => {
    setAccess(defaultAccess);
  }, [defaultAccess]);

  const reset = () => {
    setRecipient(null);
    setIsNew(false);
    setTypedPassword("");
    setGeneratePassword(false);
    setSendInviteEmail(false);
    setSendWhatsApp(true);
    setCreated(null);
    // Access, prices, viewer gate and expiry stay: the next link starts like the one just made.
  };

  const applyDefaults = (d: CatalogueShareLinkDefaults) => {
    const t = touched.current;
    const nextAccess: CatalogueSharingMode = resellerSharingOn ? d.access : "EXCLUSIVE";
    if (!t.has("access")) chooseAccess(nextAccess);
    if (!t.has("priceTier")) setPriceTier(d.priceTier && d.priceTier !== "DEFAULT" ? d.priceTier : "");
    if (!t.has("viewerGate") && viewerGateOn) setViewerGate(d.viewerGate || "NONE");
    if (!t.has("expiry") && d.expiryAmount > 0) {
      setExpiryAmount(d.expiryAmount);
      setExpiryUnit(d.expiryUnit || DEFAULT_SHARE_EXPIRY_UNIT);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      // Start clean each time the modal closes; the next open loads the last link's settings again.
      reset();
      touched.current = new Set();
      setAccess(defaultAccess);
      setViewerGate(firstGate);
      setPriceTier("");
      setExpiryAmount(DEFAULT_SHARE_EXPIRY_AMOUNT);
      setExpiryUnit(DEFAULT_SHARE_EXPIRY_UNIT);
      return;
    }
    if (!catalogue?.id) return;
    let live = true;
    setDefaults({ status: "loading" });
    fetchCatalogueShareLinkDefaults(catalogue.id)
      .then((res) => {
        if (!live) return;
        if (!res?.success || !res.data) {
          setDefaults({ status: "failed" });
          return;
        }
        applyDefaults(res.data);
        setDefaults(res.data.fromLinkAt ? { status: "last", at: res.data.fromLinkAt } : { status: "first" });
      })
      .catch(() => {
        if (live) setDefaults({ status: "failed" });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, catalogue?.id]);

  const nonIndia = recipient ? !isIndiaShareCountry(recipient.countryIso) : false;
  const openLink = resellerSharingOn && access === "OPEN";

  useEffect(() => {
    // Opening straight on Open: no person to find, so show the label fields.
    if (isOpen && openLink && !recipient && !created) {
      setRecipient({ ...EMPTY });
      setIsNew(true);
    }
  }, [isOpen, openLink, recipient, created]);
  const patch = (next: Partial<Recipient>) => setRecipient((r) => (r ? { ...r, ...next } : r));

  const pick = (party: PartyHit) => {
    // A chosen person fixes how this link opens; a late last-link default must not switch it.
    touch("access");
    const r = recipientFromParty(party);
    setRecipient(r);
    setIsNew(!r.phone);
    setSendInviteEmail(Boolean(r.email));
    if (!isIndiaShareCountry(r.countryIso)) setGeneratePassword(true);
  };

  const addNew = (query: string) => {
    touch("access");
    const q = query.trim();
    if (looksLikePhone(q)) {
      const fields = partyPhoneToShareFields(q);
      setRecipient({ ...EMPTY, phone: fields.phone, countryIso: fields.countryIso });
    } else {
      setRecipient({ ...EMPTY, name: q });
    }
    setIsNew(true);
  };

  // Open links need no number: it is only for sending the link on WhatsApp.
  const phoneSkipped = openLink && !recipient?.phone.trim();
  // An open link's name is only a label, so it may be blank too.
  const recipientReady =
    (openLink || Boolean(recipient?.name.trim())) &&
    (phoneSkipped || Boolean(recipient && isPlausibleSharePhone(recipient.phone, recipient.countryIso)));

  /** Access comes first. Open needs no person, so it starts a blank label right away. */
  const chooseAccess = (next: CatalogueSharingMode) => {
    setAccess(next);
    if (next === "OPEN" && !recipient) {
      setRecipient({ ...EMPTY });
      setIsNew(true);
    } else if (next !== "OPEN" && recipient && isNew && !recipient.name.trim() && !recipient.phone.trim()) {
      // Leaving Open with nothing typed: go back to finding the person.
      setRecipient(null);
      setIsNew(false);
    }
  };

  const create = async () => {
    if (!catalogue?.id || !recipient) return;
    const name = recipient.name.trim();
    if (!name && !openLink) {
      notifications({ title: "Name", message: "Add their name so you know who opened the link.", type: "warning" });
      return;
    }
    if (!phoneSkipped && !isPlausibleSharePhone(recipient.phone, recipient.countryIso)) {
      notifications({
        title: "Mobile",
        message: nonIndia
          ? "Enter their local mobile. The country is already selected."
          : "Enter their 10-digit mobile. Only this number can open the link.",
        type: "warning",
      });
      return;
    }
    const email = recipient.email.trim();
    if (email && !isValidShareEmail(email)) {
      notifications({ title: "Email", message: "Enter a valid email, or leave it blank.", type: "warning" });
      return;
    }
    // An open link has nothing to unlock.
    const willGenerate = !openLink && (generatePassword || (nonIndia && !typedPassword.trim()));
    const expiry = shareLinkExpiryPayload(expiryAmount, expiryUnit);
    if (!expiry) {
      notifications({ title: "Expiry", message: "Enter at least 1 minute, hour or day.", type: "warning" });
      return;
    }
    const limit = Math.round(Number(recipient.forwardLimit));
    const reseller = resellerSharingOn && access === "RESELLER";
    if (reseller && !recipient.isReseller && (!Number.isFinite(limit) || limit < 1 || limit > 50)) {
      notifications({ title: "Client shares", message: "Client shares must be between 1 and 50.", type: "warning" });
      return;
    }

    setBusy(true);
    try {
      const result = await createCatalogueShareLink(catalogue.id, {
        recipientName: name,
        recipientPhone: recipient.phone.trim(),
        recipientCountryCode: recipient.countryIso,
        recipientEmail: email || undefined,
        sendInviteEmail: Boolean(email) && sendInviteEmail,
        sendWhatsApp: whatsAppInvitesOn() ? sendWhatsApp : undefined,
        generateAccessPassword: willGenerate,
        linkPassword: willGenerate || openLink ? undefined : typedPassword.trim() || undefined,
        expiryAmount: expiry.expiryAmount,
        expiryUnit: expiry.expiryUnit,
        clientTier: priceTier.trim() || "DEFAULT",
        // Every link keeps the prices chosen here, whatever its access.
        priceTier: priceTier.trim() || undefined,
        ...(resellerSharingOn && !reseller ? { accessMode: openLink ? ("OPEN" as const) : ("EXCLUSIVE" as const) } : {}),
        ...(openLink && viewerGateOn ? { viewerGate } : {}),
        ...(reseller
          ? {
              linkRole: "RESELLER" as const,
              brandMode,
              resellerCustomerId: recipient.partyId,
              ...(recipient.isReseller ? {} : { markReseller: true, resellerForwardLimit: limit }),
            }
          : {}),
      });
      if (!result?.success || !result.data?.linkToken) {
        throw new Error(result?.message || "Could not create the link.");
      }
      setCreated({
        url: shareLinkPublicUrl({
          catalogueId: catalogue.id,
          linkToken: result.data.linkToken,
          publicUrl: result.data.publicUrl,
        }),
        password: result.data.accessPassword || "",
        emailed: Boolean(result.data.inviteEmailSent),
        whatsApped: Boolean(result.data.inviteWhatsAppSent),
        open: openLink,
        gate: openLink && viewerGateOn ? viewerGate : undefined,
        recipient: { ...recipient, name: name || "Open link" },
      });
    } catch (e: any) {
      notifications({ title: "Share", message: e?.message || "Could not create the link.", type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  // Before a person is chosen the options speak generally; Open never needs one.
  const who = recipient?.name.trim() && !openLink ? firstName(recipient.name) : "";
  const accessOptions: { value: CatalogueSharingMode; title: string; detail: string }[] = [
    {
      value: "EXCLUSIVE",
      title: "Exclusive",
      detail: who ? `Only ${who} can open it, with their mobile.` : "Only the person you invite can open it, with their mobile.",
    },
    {
      value: "RESELLER",
      title: "Reseller",
      detail: who
        ? `${who} can forward pieces to their own clients, without your name.`
        : "A reseller can forward pieces to their own clients, without your name.",
    },
    {
      value: "OPEN",
      title: "Open",
      detail: viewerGateOn
        ? "Anyone with the link can browse. You can ask their name or mobile first."
        : "Anyone with the link can browse. No name, number or code needed.",
    },
  ];

  const expiryText = shareLinkExpiryLabel(expiryAmount, expiryUnit);
  const settingsSummary = [
    openLink ? `Open link, ${viewerGateOn ? VIEWER_GATE_SHORT[viewerGate] : "opens without a code"}` : access === "RESELLER" ? "Reseller link" : "Exclusive link",
    priceTier ? `${priceTier} prices` : "product prices",
    expiryText ? `works ${expiryText}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  // A tier remembered from the last link stays selectable even if it is not a preset.
  const priceTierOptions = priceTier && !PRICE_TIER_OPTIONS.includes(priceTier as any) ? [...PRICE_TIER_OPTIONS, priceTier] : PRICE_TIER_OPTIONS;

  const defaultsLine =
    defaults.status === "loading"
      ? "Loading the settings of your last link…"
      : defaults.status === "last"
        ? `Same as your last link${shortDate(defaults.at) ? ` (${shortDate(defaults.at)})` : ""}. Change anything before you create it.`
        : defaults.status === "first"
          ? "Standard settings for a first link. Check them before you create it."
          : "Could not load your last link's settings, so these are the standard ones.";

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" className="hkp-share-modal" scrollable>
      <ModalHeader toggle={onClose}>
        {created ? "Link ready" : <>Share “{catalogue.name}”</>}
      </ModalHeader>
      <ModalBody>
        {created ? (
          <div className="hkp-share-done">
            <ShareLinkReady
              url={created.url}
              recipientName={created.recipient.name}
              phone={created.recipient.phone}
              countryIso={created.recipient.countryIso}
              catalogueName={catalogue.name}
              emailed={created.emailed}
              open={created.open}
              gate={created.gate}
              onDismiss={reset}
            />
            <ShareAccessPasswordOnce password={created.password} emailed={created.emailed} />
            {created.whatsApped ? (
              <p className="hkp-share-hint" role="status">
                ✓ Sent on WhatsApp to {created.recipient.name}. They can open the catalogue straight from the message.
              </p>
            ) : null}
            <div className="hkp-share-actions">
              <button type="button" className="hkp-share-btn is-quiet" onClick={reset}>
                Share with someone else
              </button>
              <button type="button" className="hkp-share-btn" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            {resellerSharingOn ? (
              <section className="hkp-share-step" aria-labelledby="share-access">
                <h3 className="hkp-share-step-title" id="share-access">
                  How should this link open?
                </h3>
                <div className="hkp-share-access" role="radiogroup" aria-labelledby="share-access">
                  {accessOptions.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={access === o.value}
                      className={`hkp-share-access-option${access === o.value ? " is-active" : ""}`}
                      onClick={() => {
                        touch("access");
                        chooseAccess(o.value);
                      }}
                    >
                      <span className="hkp-share-access-title">{o.title}</span>
                      <span className="hkp-share-access-detail">{o.detail}</span>
                    </button>
                  ))}
                </div>
                <p className="hkp-share-hint">Only this link. Other links to this catalogue keep their own setting.</p>
              </section>
            ) : null}

            {openLink && recipient ? (
              <OpenLinkFields
                recipient={recipient}
                onPatch={patch}
                viewerGate={viewerGateOn ? viewerGate : undefined}
                onViewerGate={
                  viewerGateOn
                    ? (g) => {
                        touch("viewerGate");
                        setViewerGate(g);
                      }
                    : undefined
                }
              />
            ) : (
              <section className="hkp-share-step" aria-labelledby="share-who">
                <h3 className="hkp-share-step-title" id="share-who">
                  Who is it for?
                </h3>
                {recipient && !isNew ? (
                  <RecipientCard recipient={recipient} onChange={() => setRecipient(null)} />
                ) : recipient && isNew ? (
                  <NewRecipientFields
                    recipient={recipient}
                    onPatch={patch}
                    onCancel={() => {
                      setRecipient(null);
                      setIsNew(false);
                    }}
                    onUseParty={(party) => pick(party)}
                  />
                ) : (
                  <PartySearch onPick={pick} onAddNew={addNew} />
                )}
              </section>
            )}

            {recipient && resellerSharingOn && access === "RESELLER" ? (
              <section className="hkp-share-step">
                <ResellerFields
                  recipient={recipient}
                  brandMode={brandMode}
                  onBrandMode={setBrandMode}
                  onLimit={(n) => patch({ forwardLimit: n })}
                />
              </section>
            ) : null}

            {/* Every setting shows before the link exists, so nothing is left to fix afterwards. */}
            <section className="hkp-share-step hkp-share-settings" aria-labelledby="share-settings">
              <h3 className="hkp-share-step-title" id="share-settings">
                Link settings
              </h3>
              <p className="hkp-share-hint" role="status">
                {defaultsLine}
              </p>
              <div className="hkp-share-settings-grid">
                <div className="hkp-share-field">
                  <Label for="share-price-tier">Prices they see</Label>
                  <Input
                    id="share-price-tier"
                    type="select"
                    value={priceTier}
                    onChange={(e) => {
                      touch("priceTier");
                      setPriceTier(e.target.value);
                    }}
                  >
                    {priceTierOptions.map((t) => (
                      <option key={t || "none"} value={t}>
                        {t === "" ? "Product default" : t}
                      </option>
                    ))}
                  </Input>
                  <small className="hkp-share-hint">Tiers are set under Catalogue → Pricing.</small>
                </div>
                <Row className="g-2 hkp-share-settings-expiry">
                  <ShareLinkExpiryFields
                    amount={expiryAmount}
                    unit={expiryUnit}
                    onAmountChange={(n) => {
                      touch("expiry");
                      setExpiryAmount(n);
                    }}
                    onUnitChange={(u) => {
                      touch("expiry");
                      setExpiryUnit(u);
                    }}
                    amountId="share-modal-expiry-amount"
                    unitId="share-modal-expiry-unit"
                  />
                </Row>
              </div>
              {openLink ? null : (
                <div className="hkp-share-field">
                  <Label for="share-link-password">Password</Label>
                  <div className="hkp-share-inline">
                    <Input
                      type="text"
                      id="share-link-password"
                      autoComplete="off"
                      value={typedPassword}
                      disabled={generatePassword}
                      onChange={(e) => setTypedPassword(e.target.value)}
                      placeholder={nonIndia ? "Required unless generated" : "Optional. OTP works for Indian numbers"}
                    />
                    <label className="hkp-share-check">
                      <input
                        type="checkbox"
                        checked={generatePassword}
                        onChange={(e) => setGeneratePassword(e.target.checked)}
                      />
                      Generate
                    </label>
                  </div>
                  {nonIndia ? (
                    <small className="hkp-share-hint">
                      SMS OTP works only for Indian numbers, so overseas buyers get a password.
                    </small>
                  ) : null}
                </div>
              )}
              {recipient?.email.trim() ? (
                <label className="hkp-share-check">
                  <input
                    type="checkbox"
                    checked={sendInviteEmail}
                    onChange={(e) => setSendInviteEmail(e.target.checked)}
                  />
                  Also email the link to {recipient.email.trim()}
                </label>
              ) : null}
              {whatsAppInvitesOn() && recipient?.phone?.trim() ? (
                <label className="hkp-share-check">
                  <input type="checkbox" checked={sendWhatsApp} onChange={(e) => setSendWhatsApp(e.target.checked)} />
                  Send the link on WhatsApp to {recipient.phone.trim()}
                </label>
              ) : null}
              <p className="hkp-share-hint hkp-share-settings-summary">
                {settingsSummary}
              </p>
            </section>

            <div className="hkp-share-actions">
              <button
                type="button"
                className="hkp-share-btn"
                onClick={() => void create()}
                disabled={busy || !recipientReady}
              >
                {busy ? "Creating link…" : "Create link"}
              </button>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

/**
 * Open link: a label for your own list and, if you like, a number to send it to. Both optional.
 * With the viewer gate on, also what the link asks each viewer before it opens.
 */
const OpenLinkFields: React.FC<{
  recipient: Recipient;
  onPatch: (next: Partial<Recipient>) => void;
  viewerGate?: CatalogueViewerGate;
  onViewerGate?: (gate: CatalogueViewerGate) => void;
}> = ({ recipient, onPatch, viewerGate, onViewerGate }) => (
  <section className="hkp-share-step" aria-label="Open link details">
    {viewerGate && onViewerGate ? (
      <div className="hkp-share-field">
        <h3 className="hkp-share-step-title" id="share-viewer-gate">
          Who is viewing?
        </h3>
        <div className="hkp-share-access" role="radiogroup" aria-labelledby="share-viewer-gate">
          {VIEWER_GATE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={viewerGate === o.value}
              className={`hkp-share-access-option${viewerGate === o.value ? " is-active" : ""}`}
              onClick={() => onViewerGate(o.value)}
            >
              <span className="hkp-share-access-title">{o.title}</span>
              <span className="hkp-share-access-detail">{o.detail}</span>
            </button>
          ))}
        </div>
        <small className="hkp-share-hint">
          Each person answers once. You then see who opened it, what they looked at and for how long, under Activity.
        </small>
      </div>
    ) : null}
    <div className="hkp-share-field">
      <Label for="share-open-name">
        Link name <span className="hkp-share-optional">optional</span>
      </Label>
      <Input
        id="share-open-name"
        autoComplete="off"
        placeholder="e.g. Instagram bio, Diwali offer"
        value={recipient.name}
        onChange={(e) => onPatch({ name: e.target.value })}
      />
      <small className="hkp-share-hint">Only you see this, in your list of links.</small>
    </div>
    <Row className="g-2">
      <ShareCountryPhoneFields
        countryIso={recipient.countryIso}
        phone={recipient.phone}
        onCountryChange={(iso) => onPatch({ countryIso: iso })}
        onPhoneChange={(phone) => onPatch({ phone })}
        countryId="share-open-country"
        phoneId="share-open-phone"
        phoneLabel="Send on WhatsApp to"
        phoneOptional
        phonePlaceholder={isIndiaShareCountry(recipient.countryIso) ? "10-digit mobile" : "Local mobile"}
      />
    </Row>
  </section>
);

/** One field for a name or a mobile; saved parties come up, otherwise a new one is added. */
const PartySearch: React.FC<{ onPick: (p: PartyHit) => void; onAddNew: (query: string) => void }> = ({
  onPick,
  onAddNew,
}) => {
  const listId = useId();
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<PartyHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(0);
  const seq = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const mine = ++seq.current;
    const q = query.trim();
    const term = looksLikePhone(q) ? q.replace(/\D/g, "").slice(-10) : q;
    const t = window.setTimeout(() => {
      setLoading(true);
      setFailed(false);
      searchParties(term)
        .then((rows) => {
          if (mine === seq.current) {
            setHits(rows);
            setActive(0);
          }
        })
        .catch(() => {
          if (mine === seq.current) {
            setHits([]);
            setFailed(true);
          }
        })
        .finally(() => {
          if (mine === seq.current) setLoading(false);
        });
    }, q ? 250 : 0);
    return () => window.clearTimeout(t);
  }, [query]);

  const canAdd = query.trim().length >= 2;
  const count = hits.length + (canAdd ? 1 : 0);
  const choose = (i: number) => {
    if (i < hits.length) onPick(hits[i]);
    else if (canAdd) onAddNew(query);
  };

  return (
    <div className="hkp-share-search">
      <Label for="share-search" className="visually-hidden">
        Name or mobile
      </Label>
      <Input
        id="share-search"
        innerRef={inputRef}
        type="search"
        autoComplete="off"
        placeholder="Search by name or mobile"
        value={query}
        role="combobox"
        aria-expanded={count > 0}
        aria-controls={listId}
        aria-activedescendant={count ? `${listId}-${active}` : undefined}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(count - 1, a + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === "Enter" && count) {
            e.preventDefault();
            choose(active);
          }
        }}
      />
      <div className="hkp-share-results" id={listId} role="listbox" aria-label="Parties">
        {hits.map((p, i) => {
          const fields = partyPhoneToShareFields(p.phone);
          return (
            <button
              type="button"
              role="option"
              id={`${listId}-${i}`}
              aria-selected={i === active}
              key={p.id}
              className={`hkp-share-result${i === active ? " is-active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(i)}
            >
              <span className="hkp-share-avatar" aria-hidden="true">
                {p.name.trim().charAt(0).toUpperCase() || "?"}
              </span>
              <span className="hkp-share-result-text">
                <strong>{p.name}</strong>
                <small>{p.phone ? formatSharePhoneDisplay(fields.phone, fields.countryIso) : "No mobile saved"}</small>
              </span>
              {p.isReseller ? <span className="hkp-share-badge">Reseller</span> : null}
            </button>
          );
        })}
        {canAdd ? (
          <button
            type="button"
            role="option"
            id={`${listId}-${hits.length}`}
            aria-selected={active === hits.length}
            className={`hkp-share-result is-new${active === hits.length ? " is-active" : ""}`}
            onMouseEnter={() => setActive(hits.length)}
            onClick={() => choose(hits.length)}
          >
            <span className="hkp-share-avatar" aria-hidden="true">
              +
            </span>
            <span className="hkp-share-result-text">
              <strong>Add “{query.trim()}”</strong>
              <small>New party, saved when you create the link</small>
            </span>
          </button>
        ) : null}
        {loading && !hits.length ? <p className="hkp-share-hint" role="status">Searching parties…</p> : null}
        {failed ? (
          <p className="hkp-share-hint" role="alert">
            Parties could not be loaded. You can still add someone new.
          </p>
        ) : null}
        {!loading && !failed && !hits.length && !canAdd ? (
          <p className="hkp-share-hint">Type a name or mobile.</p>
        ) : null}
      </div>
    </div>
  );
};

const RecipientCard: React.FC<{ recipient: Recipient; onChange: () => void }> = ({ recipient, onChange }) => (
  <div className="hkp-share-recipient">
    <span className="hkp-share-avatar is-large" aria-hidden="true">
      {recipient.name.trim().charAt(0).toUpperCase() || "?"}
    </span>
    <span className="hkp-share-result-text">
      <strong>
        {recipient.name}
        {recipient.isReseller ? <span className="hkp-share-badge">Reseller</span> : null}
      </strong>
      <small>
        {formatSharePhoneDisplay(recipient.phone, recipient.countryIso)}
        {recipient.email ? ` · ${recipient.email}` : ""}
      </small>
    </span>
    <button type="button" className="hkp-share-link-btn" onClick={onChange}>
      Change
    </button>
  </div>
);

const NewRecipientFields: React.FC<{
  recipient: Recipient;
  onPatch: (next: Partial<Recipient>) => void;
  onCancel: () => void;
  onUseParty: (p: PartyHit) => void;
}> = ({ recipient, onPatch, onCancel, onUseParty }) => (
  <div className="hkp-share-new">
    <div className="hkp-share-new-head">
      <span>New party</span>
      <button type="button" className="hkp-share-link-btn" onClick={onCancel}>
        Search again
      </button>
    </div>
    <div className="hkp-share-field">
      <Label for="share-new-name">Name</Label>
      <Input
        id="share-new-name"
        autoComplete="off"
        placeholder="Person or business"
        value={recipient.name}
        onChange={(e) => onPatch({ name: e.target.value })}
      />
    </div>
    <Row className="g-2">
      <ShareCountryPhoneFields
        countryIso={recipient.countryIso}
        phone={recipient.phone}
        onCountryChange={(iso) => onPatch({ countryIso: iso })}
        onPhoneChange={(phone) => onPatch({ phone })}
        countryId="share-new-country"
        phoneId="share-new-phone"
        phonePlaceholder={isIndiaShareCountry(recipient.countryIso) ? "10-digit mobile" : "Local mobile"}
      />
    </Row>
    <SharePartyMatch
      phone={recipient.phone}
      countryIso={recipient.countryIso}
      name={recipient.name}
      onUseParty={(party) => {
        // A saved party has this number: switch to it instead of adding a duplicate.
        void searchParties(party.phone.replace(/\D/g, "").slice(-10))
          .then((rows) => {
            const hit = rows.find((r) => r.name === party.name) || rows[0];
            if (hit) onUseParty(hit);
            else onPatch({ name: party.name, phone: party.phone, countryIso: party.countryIso, email: party.email });
          })
          .catch(() => onPatch({ name: party.name, phone: party.phone, countryIso: party.countryIso, email: party.email }));
      }}
    />
    <div className="hkp-share-field">
      <Label for="share-new-email">
        Email <span className="hkp-share-optional">optional</span>
      </Label>
      <Input
        id="share-new-email"
        type="email"
        autoComplete="off"
        placeholder="name@company.com"
        value={recipient.email}
        onChange={(e) => onPatch({ email: e.target.value })}
      />
    </div>
  </div>
);

const ResellerFields: React.FC<{
  recipient: Recipient;
  brandMode: CatalogueBrandMode;
  onBrandMode: (m: CatalogueBrandMode) => void;
  onLimit: (n: number) => void;
}> = ({ recipient, brandMode, onBrandMode, onLimit }) => (
  <div className="hkp-share-reseller">
    {recipient.isReseller ? (
      <p className="hkp-share-note">
        {firstName(recipient.name)} can share with up to {recipient.forwardLimit} clients (set in Parties).
      </p>
    ) : (
      <div className="hkp-share-field">
        <Label for="share-forward-limit">Clients they can share with</Label>
        <Input
          id="share-forward-limit"
          type="number"
          inputMode="numeric"
          min={1}
          max={50}
          value={recipient.forwardLimit}
          onChange={(e) => onLimit(Number(e.target.value))}
          className="hkp-share-number"
        />
        <small className="hkp-share-hint">
          {recipient.partyId ? recipient.name : "This new party"} will be marked as a reseller in Parties.
        </small>
      </div>
    )}
    <fieldset className="hkp-share-field">
      <legend className="form-label">Their clients see</legend>
      <div className="hkp-share-segment" role="radiogroup">
        <button
          type="button"
          role="radio"
          aria-checked={brandMode === "RESELLER"}
          className={brandMode === "RESELLER" ? "is-active" : ""}
          onClick={() => onBrandMode("RESELLER")}
        >
          {firstName(recipient.name)}'s name
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={brandMode === "UNBRANDED"}
          className={brandMode === "UNBRANDED" ? "is-active" : ""}
          onClick={() => onBrandMode("UNBRANDED")}
        >
          No name
        </button>
      </div>
      <small className="hkp-share-hint">Your studio's name, logo and contact are never shown to their clients.</small>
    </fieldset>
  </div>
);

export default ShareInviteModal;
